/**
 * Shared `<Button />` — token-driven via `@rynora/theme`.
 *
 * Hierarchy: primary / secondary / ghost (tertiary) / danger / link.
 * Sizes: sm / md / lg / icon (square icon-only).
 */

import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  /** When true, sets aria-busy and shows the loading affordance. */
  loading?: boolean;
  children?: ReactNode;
}

const VARIANT_CLASSNAME: Record<ButtonVariant, string> = {
  primary: 'rui-btn rui-btn--primary',
  secondary: 'rui-btn rui-btn--secondary',
  ghost: 'rui-btn rui-btn--ghost',
  danger: 'rui-btn rui-btn--danger',
  link: 'rui-btn rui-btn--link',
};

const SIZE_CLASSNAME: Record<ButtonSize, string> = {
  sm: 'rui-btn--sm',
  md: 'rui-btn--md',
  lg: 'rui-btn--lg',
  icon: 'rui-btn--icon',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    loading = false,
    disabled,
    className,
    type,
    children,
    ...rest
  } = props;
  const cls = [
    VARIANT_CLASSNAME[variant],
    SIZE_CLASSNAME[size],
    fullWidth ? 'rui-btn--full' : null,
    loading ? 'rui-btn--loading' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cls}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="rui-btn__spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
});
